<template>
  <div>
    <Breadcrumbs main="SubCategory Closure" title="Pending Approvals" />
    <div class="container-fluid">
      <div class="row">
        <div class="col-lg-12">
          <div class="card">
            <div class="card-header">
              <h5>SubCategory Closure Requests - Admin Dashboard</h5>
              <div class="card-header-right">
                <ul class="list-unstyled card-option">
                  <li><i class="fa fa-spin fa-cog"></i></li>
                  <li><i class="fa fa-th"></i></li>
                  <li><i class="fa fa-times"></i></li>
                </ul>
              </div>
            </div>

            <!-- Filters -->
            <div class="card-body">
              <div class="row mb-3">
                <div class="col-md-2">
                  <label class="form-label">View Mode</label>
                  <select v-model="viewMode" class="form-control" @change="onViewModeChange">
                    <option value="pending">Pending Only</option>
                    <option value="all">All History</option>
                  </select>
                </div>
                <div class="col-md-2" v-if="viewMode === 'all'">
                  <label class="form-label">Status Filter</label>
                  <select v-model="filters.status" class="form-control" @change="applyFilters">
                    <option value="">All Statuses</option>
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
                <div class="col-md-2">
                  <label class="form-label">From Date</label>
                  <input 
                    v-model="filters.fromDate" 
                    type="date" 
                    class="form-control" 
                    @change="applyFilters"
                  />
                </div>
                <div class="col-md-2">
                  <label class="form-label">To Date</label>
                  <input 
                    v-model="filters.toDate" 
                    type="date" 
                    class="form-control" 
                    @change="applyFilters"
                  />
                </div>
                <div class="col-md-2">
                  <label class="form-label">Site Filter</label>
                  <select v-model="filters.siteFilter" class="form-control" @change="applyFilters">
                    <option value="">All Sites</option>
                    <option v-for="site in uniqueSites" :key="site" :value="site">{{ site }}</option>
                  </select>
                </div>
                <div class="col-md-2">
                  <label class="form-label">Search</label>
                  <input 
                    v-model="searchQuery" 
                    type="text" 
                    class="form-control" 
                    placeholder="Search by log number, site, employee..."
                    @input="applySearch"
                  />
                </div>
              </div>
              <div class="row mb-3">
                <div class="col-md-12 d-flex justify-content-end">
                  <button @click="clearFilters" class="btn btn-secondary me-2">Clear Filters</button>
                  <button 
                    @click="refreshData" 
                    class="btn btn-primary"
                    :disabled="loading"
                  >
                    <i class="fa fa-refresh" :class="{ 'fa-spin': loading }"></i>
                    Refresh Data
                  </button>
                </div>
              </div>

              <!-- Summary Cards -->
              <div class="row mb-4">
                <div class="col-md-3">
                  <div class="card bg-danger text-white">
                    <div class="card-body">
                      <div class="d-flex justify-content-between">
                        <div>
                          <h4>{{ viewMode === 'pending' ? overduePendingRequests.length : summary.pending }}</h4>
                          <p class="mb-0">{{ viewMode === 'pending' ? 'Overdue Requests' : 'Pending' }}</p>
                        </div>
                        <div class="align-self-center">
                          <i class="fa fa-exclamation-triangle fa-2x"></i>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="card bg-warning text-white">
                    <div class="card-body">
                      <div class="d-flex justify-content-between">
                        <div>
                          <h4>{{ viewMode === 'pending' ? urgentRequests.length : summary.rejected }}</h4>
                          <p class="mb-0">{{ viewMode === 'pending' ? 'Urgent Requests' : 'Rejected' }}</p>
                        </div>
                        <div class="align-self-center">
                          <i class="fa fa-times-circle fa-2x"></i>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="card bg-success text-white">
                    <div class="card-body">
                      <div class="d-flex justify-content-between">
                        <div>
                          <h4>{{ viewMode === 'pending' ? pendingRequests.regular.length : summary.approved }}</h4>
                          <p class="mb-0">{{ viewMode === 'pending' ? 'Regular Requests' : 'Approved' }}</p>
                        </div>
                        <div class="align-self-center">
                          <i class="fa fa-check-circle fa-2x"></i>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="card bg-info text-white">
                    <div class="card-body">
                      <div class="d-flex justify-content-between">
                        <div>
                          <h4>{{ pendingRequests.total }}</h4>
                          <p class="mb-0">Total {{ viewMode === 'pending' ? 'Pending' : 'Requests' }}</p>
                        </div>
                        <div class="align-self-center">
                          <i class="fa fa-list fa-2x"></i>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Bulk Actions -->
              <div class="row mb-3" v-if="selectedRequests.length > 0 && viewMode === 'pending'">
                <div class="col-12">
                  <div class="alert alert-info">
                    <strong>{{ selectedRequests.length }}</strong> request(s) selected.
                    <button @click="openBulkApprovalModal" class="btn btn-sm btn-success ms-2">
                      <i class="fa fa-check"></i> Bulk Approve
                    </button>
                    <button @click="clearSelection" class="btn btn-sm btn-secondary ms-2">
                      <i class="fa fa-times"></i> Clear Selection
                    </button>
                  </div>
                </div>
              </div>

              <!-- Pending Requests Table -->
              <div class="table-responsive">
                <table class="table table-bordered table-striped">
                  <thead>
                    <tr>
                      <th>
                        <input 
                          type="checkbox" 
                          @change="toggleSelectAll"
                          :checked="selectAllChecked"
                          class="form-check-input"
                        >
                      </th>
                      <th>Log Number</th>
                      <th>SubCategory Info</th>
                      <th>Site Details</th>
                      <th>Assignments</th>
                      <th>Status</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <!-- Overdue Requests -->
                    <tr v-for="request in overduePendingRequests" :key="request._id">
                      <td>
                        <input 
                          type="checkbox" 
                          :value="request._id"
                          v-model="selectedRequests"
                          class="form-check-input"
                          :disabled="request.status !== 'pending'"
                        >
                      </td>
                      <td>
                        <router-link 
                          v-if="request.kytOperationId?.mainLogNo"
                          :to="`/kyt-chain/logs/${request.kytOperationId.mainLogNo}/kyts`"
                          class="text-decoration-none fw-bold text-primary"
                          :title="`View KYT details for ${request.kytOperationId.mainLogNo}`"
                        >
                          {{ request.subCategoryLogNo }}
                        </router-link>
                        <strong v-else>{{ request.subCategoryLogNo }}</strong>
                        <br>
                        <small class="text-muted">{{ request.subcategoryDetails?.subCategoryInfo?.categoryType?.toUpperCase() || 'N/A' }}</small>
                        <br>
                        <small v-if="request.kytOperationId?.mainLogNo" class="text-info">
                          <i class="fa fa-link"></i> {{ request.kytOperationId.mainLogNo }}
                        </small>
                      </td>
                      <td>
                        <div>
                          <strong>{{ request.subCategoryId?.name || request.subCategoryName || 'N/A' }}</strong>
                          <br>
                          <small class="text-muted">{{ request.subCategoryId?.CategoryId?.name || 'N/A' }}</small>
                          <br>
                          <small class="text-warning">{{ request.subcategoryDetails?.subCategoryInfo?.problem || 'No problem description' }}</small>
                        </div>
                      </td>
                      <td>
                        <div>
                          <strong>{{ request.subcategoryDetails?.siteInformation?.siteName || request.kytOperationId?.siteName || 'N/A' }}</strong>
                          <br>
                          <small class="text-muted">{{ request.subcategoryDetails?.siteInformation?.siteAddress || request.kytOperationId?.siteAddress || 'N/A' }}</small>
                          <br>
                          <small class="text-info">Manager: {{ request.subcategoryDetails?.siteInformation?.kytDetails?.assignedManager?.name || request.kytOperationId?.empId?.employeeName || 'N/A' }}</small>
                        </div>
                      </td>
                      <td>
                        <div>
                          <span class="badge bg-primary">{{ request.totalAssignments || 0 }} Total</span>
                          <br>
                          <span class="badge bg-success mt-1">{{ request.completedAssignments || 0 }} Complete</span>
                          <br>
                          <span class="badge bg-warning mt-1">{{ (request.totalAssignments || 0) - (request.completedAssignments || 0) }} Pending</span>
                          <br>
                          <small class="text-muted">
                            {{ request.subcategoryDetails?.assignedEmployees?.map(emp => emp.employee?.name || 'N/A').join(', ') || 'No employees assigned' }}
                          </small>
                        </div>
                      </td>
                      <td>
                        <span 
                          class="badge"
                          :class="getStatusClass(request.status)"
                        >
                          {{ request.status?.toUpperCase() || 'PENDING' }}
                        </span>
                        <br>
                        <small class="text-muted">{{ formatDate(request.createdAt) }}</small>
                        <br>
                        <span v-if="request.subcategoryDetails?.subCategoryInfo?.images?.length" class="badge bg-info mt-1">
                          {{ request.subcategoryDetails.subCategoryInfo.images.length }} Images
                        </span>
                        <span v-if="request.isUrgent" class="badge bg-warning mt-1">URGENT</span>
                      </td>
                      <td>{{ formatDate(request.createdAt) }}</td>
                      <td>
                        <div class="btn-group btn-group-sm">
                          <button 
                            @click="openDetailsModal(request)"
                            class="btn btn-info"
                            title="View Details"
                          >
                            <i class="fa fa-eye"></i>
                          </button>
                          <button 
                            v-if="request.status === 'pending'"
                            @click="openApprovalModal(request)"
                            class="btn btn-success"
                            title="Approve"
                          >
                            <i class="fa fa-check"></i>
                          </button>
                          <button 
                            v-if="request.status === 'pending'"
                            @click="openRejectionModal(request)"
                            class="btn btn-danger"
                            title="Reject"
                          >
                            <i class="fa fa-times"></i>
                          </button>
                        </div>
                      </td>
                    </tr>

                    <!-- Regular Requests -->
                    <tr v-for="request in pendingRequests.regular" :key="request._id">
                      <td>
                        <input 
                          type="checkbox" 
                          :value="request._id"
                          v-model="selectedRequests"
                          class="form-check-input"
                          :disabled="request.status !== 'pending'"
                        >
                      </td>
                      <td>
                        <router-link 
                          v-if="request.kytOperationId?.mainLogNo"
                          :to="`/kyt-chain/logs/${request.kytOperationId.mainLogNo}/kyts`"
                          class="text-decoration-none fw-bold text-primary"
                          :title="`View KYT details for ${request.kytOperationId.mainLogNo}`"
                        >
                          {{ request.subCategoryLogNo }}
                        </router-link>
                        <strong v-else>{{ request.subCategoryLogNo }}</strong>
                        <br>
                        <small class="text-muted">{{ request.subcategoryDetails?.subCategoryInfo?.categoryType?.toUpperCase() || 'N/A' }}</small>
                        <br>
                        <small v-if="request.kytOperationId?.mainLogNo" class="text-info">
                          <i class="fa fa-link"></i> {{ request.kytOperationId.mainLogNo }}
                        </small>
                      </td>
                      <td>
                        <div>
                          <strong>{{ request.subCategoryId?.name || request.subCategoryName || 'N/A' }}</strong>
                          <br>
                          <small class="text-muted">{{ request.subCategoryId?.CategoryId?.name || 'N/A' }}</small>
                          <br>
                          <small class="text-warning">{{ request.subcategoryDetails?.subCategoryInfo?.problem || 'No problem description' }}</small>
                        </div>
                      </td>
                      <td>
                        <div>
                          <strong>{{ request.subcategoryDetails?.siteInformation?.siteName || request.kytOperationId?.siteName || 'N/A' }}</strong>
                          <br>
                          <small class="text-muted">{{ request.subcategoryDetails?.siteInformation?.siteAddress || request.kytOperationId?.siteAddress || 'N/A' }}</small>
                          <br>
                          <small class="text-info">Manager: {{ request.subcategoryDetails?.siteInformation?.kytDetails?.assignedManager?.name || request.kytOperationId?.empId?.employeeName || 'N/A' }}</small>
                        </div>
                      </td>
                      <td>
                        <div>
                          <span class="badge bg-primary">{{ request.totalAssignments || 0 }} Total</span>
                          <br>
                          <span class="badge bg-success mt-1">{{ request.completedAssignments || 0 }} Complete</span>
                          <br>
                          <span class="badge bg-warning mt-1">{{ (request.totalAssignments || 0) - (request.completedAssignments || 0) }} Pending</span>
                          <br>
                          <small class="text-muted">
                            {{ request.subcategoryDetails?.assignedEmployees?.map(emp => emp.employee?.name || 'N/A').join(', ') || 'No employees assigned' }}
                          </small>
                        </div>
                      </td>
                      <td>
                        <span 
                          class="badge"
                          :class="getStatusClass(request.status)"
                        >
                          {{ request.status?.toUpperCase() || 'PENDING' }}
                        </span>
                        <br>
                        <small class="text-muted">{{ formatDate(request.createdAt) }}</small>
                        <br>
                        <span v-if="request.subcategoryDetails?.subCategoryInfo?.images?.length" class="badge bg-info mt-1">
                          {{ request.subcategoryDetails.subCategoryInfo.images.length }} Images
                        </span>
                        <span v-if="request.isUrgent" class="badge bg-warning mt-1">URGENT</span>
                      </td>
                      <td>{{ formatDate(request.createdAt) }}</td>
                      <td>
                        <div class="btn-group btn-group-sm">
                          <button 
                            @click="openDetailsModal(request)"
                            class="btn btn-info"
                            title="View Details"
                          >
                            <i class="fa fa-eye"></i>
                          </button>
                          <button 
                            v-if="request.status === 'pending'"
                            @click="openApprovalModal(request)"
                            class="btn btn-success"
                            title="Approve"
                          >
                            <i class="fa fa-check"></i>
                          </button>
                          <button 
                            v-if="request.status === 'pending'"
                            @click="openRejectionModal(request)"
                            class="btn btn-danger"
                            title="Reject"
                          >
                            <i class="fa fa-times"></i>
                          </button>
                        </div>
                      </td>
                    </tr>

                    <!-- No data -->
                    <tr v-if="pendingRequests.total === 0 && !loading">
                      <td colspan="8" class="text-center py-4">
                        <div class="text-muted">
                          <i class="fa fa-inbox fa-3x mb-3"></i>
                          <p>{{ viewMode === 'pending' ? 'No pending closure requests found.' : 'No closure requests found.' }}</p>
                        </div>
                      </td>
                    </tr>

                    <!-- Loading -->
                    <tr v-if="loading">
                      <td colspan="8" class="text-center py-4">
                        <div class="text-muted">
                          <i class="fa fa-spinner fa-spin fa-2x"></i>
                          <p class="mt-2">Loading pending requests...</p>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modals -->
    <RequestDetailsModal 
      v-if="showDetailsModal"
      :request="selectedRequest"
      @close="closeDetailsModal"
    />

    <ApprovalModal 
      v-if="showApprovalModal"
      :request="selectedRequest"
      @approve="handleApprove"
      @close="closeApprovalModal"
    />

    <RejectionModal 
      v-if="showRejectionModal"
      :request="selectedRequest"
      @reject="handleReject"
      @close="closeRejectionModal"
    />

    <BulkApprovalModal 
      v-if="showBulkApprovalModal"
      :requests="selectedRequestObjects"
      @approve="handleBulkApprove"
      @close="closeBulkApprovalModal"
    />
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import Breadcrumbs from '@/layout/breadCrumbs.vue';
import RequestDetailsModal from '@/components/subcategory-closure/RequestDetailsModal.vue';
import ApprovalModal from '@/components/subcategory-closure/ApprovalModal.vue';
import RejectionModal from '@/components/subcategory-closure/RejectionModal.vue';
import BulkApprovalModal from '@/components/subcategory-closure/BulkApprovalModal.vue';
import { 
  getPendingApprovals, 
  getAllClosureRequests,
  approveClosureRequest, 
  rejectClosureRequest,
  formatDate,
  isOverdue,
  PRIORITY_CONFIG
} from '@/services/subcategoryClosureService';
import { useToast } from 'vue-toast-notification';
import 'vue-toast-notification/dist/theme-default.css';

// Reactive data
const loading = ref(false);
const pendingRequests = ref({ overdue: [], regular: [], total: 0 });
const selectedRequests = ref([]);
const selectedRequest = ref(null);
const searchQuery = ref('');
const viewMode = ref('pending'); // 'pending' or 'all'
const summary = ref({ pending: 0, approved: 0, rejected: 0, cancelled: 0 });
const toast = useToast();

// Filters
const filters = ref({
  status: '',
  fromDate: '',
  toDate: '',
  siteFilter: ''
});

// Modal states
const showDetailsModal = ref(false);
const showApprovalModal = ref(false);
const showRejectionModal = ref(false);
const showBulkApprovalModal = ref(false);

// Computed properties
const overduePendingRequests = computed(() => {
  const allRequests = [...pendingRequests.value.overdue, ...pendingRequests.value.regular];
  return allRequests.filter(request => isOverdue(request.createdAt));
});

const urgentRequests = computed(() => {
  const allRequests = [...pendingRequests.value.overdue, ...pendingRequests.value.regular];
  return allRequests.filter(request => request.isUrgent);
});

const selectAllChecked = computed(() => {
  const allRequests = [...pendingRequests.value.overdue, ...pendingRequests.value.regular];
  return allRequests.length > 0 && selectedRequests.value.length === allRequests.length;
});

const selectedRequestObjects = computed(() => {
  const allRequests = [...pendingRequests.value.overdue, ...pendingRequests.value.regular];
  return allRequests.filter(request => selectedRequests.value.includes(request._id));
});

// Get unique sites for filter dropdown
const uniqueSites = computed(() => {
  const allRequests = [...pendingRequests.value.overdue, ...pendingRequests.value.regular];
  const sites = allRequests.map(request => 
    request.subcategoryDetails?.siteInformation?.siteName || 
    request.kytOperationId?.siteName
  ).filter(Boolean);
  return [...new Set(sites)].sort();
});

// Methods
const fetchPendingRequests = async () => {
  loading.value = true;
  try {
    let response;
    if (viewMode.value === 'pending') {
      response = await getPendingApprovals(filters.value);
    } else {
      response = await getAllClosureRequests(filters.value);
    }
    
    if (response?.data?.message && response.data.closureRequests) {
      // Transform the new API response to match our component structure
      let allRequests = response.data.closureRequests || [];
      
      // Apply status filter if in all mode
      if (viewMode.value === 'all' && filters.value.status) {
        allRequests = allRequests.filter(request => request.status === filters.value.status);
      }
      
      // Apply date filter
      if (filters.value.fromDate || filters.value.toDate) {
        allRequests = allRequests.filter(request => 
          isDateInRange(request.createdAt, filters.value.fromDate, filters.value.toDate)
        );
      }
      
      // Apply site filter
      if (filters.value.siteFilter) {
        allRequests = allRequests.filter(request => {
          const siteName = request.subcategoryDetails?.siteInformation?.siteName || 
                          request.kytOperationId?.siteName || '';
          return siteName === filters.value.siteFilter;
        });
      }
      
      const regularRequests = allRequests.filter(request => !isOverdue(request.createdAt));
      const overdueRequests = allRequests.filter(request => isOverdue(request.createdAt));
      
      pendingRequests.value = {
        regular: regularRequests,
        overdue: overdueRequests,
        total: allRequests.length
      };
      
      // Update summary from API response
      if (response.data.summary) {
        summary.value = response.data.summary;
      }
    } else {
      console.error('API Error:', response?.data);
      toast.error(response?.data?.message || 'Failed to fetch pending requests');
    }
  } catch (error) {
    console.error('Error fetching pending requests:', error);
    toast.error('Error loading pending requests');
    // Reset data on error
    pendingRequests.value = { overdue: [], regular: [], total: 0 };
  } finally {
    loading.value = false;
  }
};

const applyFilters = () => {
  selectedRequests.value = [];
  fetchPendingRequests();
};

const applySearch = () => {
  // If we have a search query, filter the current data
  if (searchQuery.value.trim()) {
    const query = searchQuery.value.toLowerCase().trim();
    const allRequests = [...pendingRequests.value.overdue, ...pendingRequests.value.regular];
    
    const filteredRequests = allRequests.filter(request => {
      const logNo = request.subCategoryLogNo?.toLowerCase() || '';
      const subCategoryName = request.subCategoryId?.name?.toLowerCase() || request.subCategoryName?.toLowerCase() || '';
      const categoryName = request.subCategoryId?.CategoryId?.name?.toLowerCase() || '';
      const siteName = request.subcategoryDetails?.siteInformation?.siteName?.toLowerCase() || request.kytOperationId?.siteName?.toLowerCase() || '';
      const employeeNames = request.subcategoryDetails?.assignedEmployees?.map(emp => emp.employee?.name?.toLowerCase() || 'n/a').join(' ') || '';
      const managerName = request.subcategoryDetails?.siteInformation?.kytDetails?.assignedManager?.name?.toLowerCase() || request.kytOperationId?.empId?.employeeName?.toLowerCase() || '';
      const problem = request.subcategoryDetails?.subCategoryInfo?.problem?.toLowerCase() || '';
      
      return logNo.includes(query) || 
             subCategoryName.includes(query) || 
             categoryName.includes(query) || 
             siteName.includes(query) || 
             employeeNames.includes(query) || 
             managerName.includes(query) || 
             problem.includes(query);
    });
    
    // Separate into overdue and regular
    const regularFiltered = filteredRequests.filter(request => !isOverdue(request.createdAt));
    const overdueFiltered = filteredRequests.filter(request => isOverdue(request.createdAt));
    
    pendingRequests.value = {
      regular: regularFiltered,
      overdue: overdueFiltered,
      total: filteredRequests.length
    };
  } else {
    // No search query, fetch fresh data
    fetchPendingRequests();
  }
};

const isDateInRange = (dateString, fromDate, toDate) => {
  if (!fromDate && !toDate) return true;
  
  const date = new Date(dateString);
  const from = fromDate ? new Date(fromDate + 'T00:00:00') : null;
  const to = toDate ? new Date(toDate + 'T23:59:59') : null;
  
  if (from && to) {
    return date >= from && date <= to;
  } else if (from) {
    return date >= from;
  } else if (to) {
    return date <= to;
  }
  
  return true;
};

const clearFilters = () => {
  filters.value = {
    status: '',
    fromDate: '',
    toDate: '',
    siteFilter: ''
  };
  searchQuery.value = '';
  selectedRequests.value = [];
  fetchPendingRequests();
};

const onViewModeChange = () => {
  // Clear status filter when switching modes
  filters.value.status = '';
  selectedRequests.value = [];
  fetchPendingRequests();
};

const getPriorityClass = (priority) => {
  return PRIORITY_CONFIG[priority]?.class || PRIORITY_CONFIG.medium.class;
};

const getStatusClass = (status) => {
  const statusConfig = {
    pending: 'bg-warning text-dark',
    approved: 'bg-success',
    rejected: 'bg-danger',
    cancelled: 'bg-secondary'
  };
  return statusConfig[status] || 'bg-warning text-dark';
};

const getProgressBarClass = (percentage) => {
  if (percentage === 0) return 'bg-secondary';
  if (percentage < 25) return 'bg-danger';
  if (percentage < 50) return 'bg-warning';
  if (percentage < 75) return 'bg-info';
  return 'bg-success';
};

const refreshData = () => {
  selectedRequests.value = [];
  fetchPendingRequests();
};

const toggleSelectAll = () => {
  const allRequests = [...pendingRequests.value.overdue, ...pendingRequests.value.regular];
  const pendingOnlyRequests = allRequests.filter(request => request.status === 'pending');
  const selectableRequests = viewMode.value === 'pending' ? allRequests : pendingOnlyRequests;
  
  if (selectedRequests.value.length === selectableRequests.length) {
    selectedRequests.value = [];
  } else {
    selectedRequests.value = selectableRequests.map(request => request._id);
  }
};

const clearSelection = () => {
  selectedRequests.value = [];
};

// Modal methods
const openDetailsModal = (request) => {
  selectedRequest.value = request;
  showDetailsModal.value = true;
};

const closeDetailsModal = () => {
  showDetailsModal.value = false;
  selectedRequest.value = null;
};

const openApprovalModal = (request) => {
  selectedRequest.value = request;
  showApprovalModal.value = true;
};

const closeApprovalModal = () => {
  showApprovalModal.value = false;
  selectedRequest.value = null;
};

const openRejectionModal = (request) => {
  selectedRequest.value = request;
  showRejectionModal.value = true;
};

const closeRejectionModal = () => {
  showRejectionModal.value = false;
  selectedRequest.value = null;
};

const openBulkApprovalModal = () => {
  showBulkApprovalModal.value = true;
};

const closeBulkApprovalModal = () => {
  showBulkApprovalModal.value = false;
};

// Action handlers
const handleApprove = async (requestId, comments) => {
  try {
    loading.value = true;
    const response = await approveClosureRequest(requestId, comments);
    if (response?.data?.success) {
      toast.success('Request approved successfully');
      closeApprovalModal();
      await fetchPendingRequests();
    } else {
      toast.error(response?.data?.message || 'Failed to approve request');
    }
  } catch (error) {
    console.error('Error approving request:', error);
    toast.error('Error approving request');
  } finally {
    loading.value = false;
  }
};

const handleReject = async (requestId, reason) => {
  try {
    loading.value = true;
    const response = await rejectClosureRequest(requestId, reason);
    if (response?.data?.success) {
      toast.success('Request rejected successfully');
      closeRejectionModal();
      await fetchPendingRequests();
    } else {
      toast.error(response?.data?.message || 'Failed to reject request');
    }
  } catch (error) {
    console.error('Error rejecting request:', error);
    toast.error('Error rejecting request');
  } finally {
    loading.value = false;
  }
};

const handleBulkApprove = async (requestIds, comments) => {
  try {
    loading.value = true;
    const { bulkApproveRequests } = await import('@/services/subcategoryClosureService');
    const response = await bulkApproveRequests(requestIds, comments);
    
    if (response?.data?.success) {
      const results = response.data.results;
      toast.success(`Bulk approval completed: ${results.approved} approved, ${results.failed} failed`);
      closeBulkApprovalModal();
      selectedRequests.value = [];
      await fetchPendingRequests();
    } else {
      toast.error(response?.data?.message || 'Bulk approval failed');
    }
  } catch (error) {
    console.error('Error in bulk approval:', error);
    toast.error('Error in bulk approval');
  } finally {
    loading.value = false;
  }
};

// Lifecycle
onMounted(() => {
  fetchPendingRequests();
});
</script>

<style scoped>
.table-danger {
  background-color: rgba(220, 53, 69, 0.1);
}

.btn-group-sm .btn {
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
}

.alert {
  border-radius: 0.375rem;
}

.badge {
  font-size: 0.75rem;
}

.card {
  box-shadow: 0 0 35px 0 rgba(154, 161, 171, 0.15);
  border: 1px solid #e5e9f2;
}

.table th {
  border-top: none;
  background-color: #f8f9fa;
  font-weight: 600;
}

.form-check-input:checked {
  background-color: #3e5fce;
  border-color: #3e5fce;
}

.progress {
  border-radius: 10px;
}

.progress-bar {
  border-radius: 10px;
  font-weight: 600;
  font-size: 0.75rem;
}

.table td {
  vertical-align: middle;
}

.table td small {
  display: block;
  line-height: 1.2;
}

.badge.mt-1 {
  margin-top: 0.25rem !important;
}

.text-warning {
  color: #f39c12 !important;
}

.text-info {
  color: #3498db !important;
}

/* Clickable log number styles */
.text-primary {
  color: #3e5fce !important;
  transition: all 0.2s ease;
}

.text-primary:hover {
  color: #2847a8 !important;
  text-decoration: underline !important;
}

.fw-bold {
  font-weight: 600 !important;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .table-responsive {
    font-size: 0.875rem;
  }
  
  .badge {
    font-size: 0.65rem;
  }
  
  .btn-group-sm .btn {
    padding: 0.2rem 0.4rem;
    font-size: 0.75rem;
  }
}
</style>